import React from 'react';
import {useMutation} from '@apollo/client';
import {Form, Input, Modal, Space} from 'antd';
import {MaskedInput} from 'antd-mask-input';
import cn from 'classnames';

import {Card} from '../../../components/Card/Card';
import {T2Button} from '../../../components/T2Button/T2Button';
import {OFFER_ITK, OFFER_POLICY, OFFER_T2} from '../../../config/routes';
import {
    t2ConfirmSubscription,
    t2ConfirmSubscriptionVariables
} from '../../../shared/graphql/__generated__/t2ConfirmSubscription';
import {
    t2InitSubscription,
    t2InitSubscriptionVariables
} from '../../../shared/graphql/__generated__/t2InitSubscription';
import {CONFIRM_SUBSCRIPTION, INIT_SUBSCRIPTION} from '../../../shared/graphql/subscription';
import {phoneToPlain} from '../../../shared/helpers/phoneFormat';
import {phoneRegex} from '../../../shared/helpers/regex';

import img_guard from './guard.png'

import cls from './Connect.module.scss'

interface IFormInit {
    phone: string;
    email: string;
}

interface IFormConfirm {
    code: string;
}

export const Connect = () => {
    
    const [confirmOpen, setConfirmOpen] = React.useState(false);
    const [successOpen, setSuccessOpen] = React.useState(false);
    const [confirmationId, setConfirmationId] = React.useState<string>('');
    const [errorOpen, setErrorOpen] = React.useState(false);
    const [errorMessage, setErrorMessage] = React.useState('');
    const [errorTitle, setErrorTitle] = React.useState('');
    const [formInit] = Form.useForm();
    const [formConfirm] = Form.useForm();

    const [init, initData] = useMutation<t2InitSubscription,t2InitSubscriptionVariables>(INIT_SUBSCRIPTION)
    const [confirm, confirmData] = useMutation<t2ConfirmSubscription,t2ConfirmSubscriptionVariables>(CONFIRM_SUBSCRIPTION)

    const onInit = async (data:IFormInit) => {
        await init({
            variables: {
                data: {
                    email: data.email,
                    phone: phoneToPlain(data.phone),
                    policy: 'ppm_t2'
                }
            },
            onCompleted: data => {
                if (data.t2InitSubscription?.confirmationId) {
                    setConfirmationId(data.t2InitSubscription.confirmationId!);
                    setConfirmOpen(true);
                }
            },
            onError: error => {
                if (error.message.includes('is already active')) {
                    setErrorOpen(true);
                    setErrorTitle('Уже активна');
                    setErrorMessage('Подписка для этого номера уже активна');
                } else {
                    setErrorOpen(true);
                    setErrorTitle('Ошибка');
                    setErrorMessage('Ошибка при инициализации подписки');
                }
            }
        })
    }

    const onConfirm = async (data:IFormConfirm) => {
        await confirm({
            variables: {
                data: {
                    code: data.code,
                    id: confirmationId,
                }
            },
            onCompleted: data => {
                if (data.t2ConfirmSubscription?.done) {
                    formInit.resetFields();
                    formConfirm.resetFields();
                    setConfirmOpen(false);
                    setSuccessOpen(true);
                }
            },
            onError: error => {
                if (error.message.includes('Incorrect code')) {
                    setErrorTitle('Не верный код');
                    setErrorMessage('Не верный код подтверждения');
                } else {
                    setErrorOpen(true);
                    setErrorTitle('Ошибка');
                    setErrorMessage('Ошибка при инициализации подписки');
                }
            }
        })
    }

    return (
        <div className={cls.cards}>
            <Card color={'gray'} className={cls.formWrapper} id={'connect'}>
                <div className={cls.wrapper}>
                    <h2>Подключите услугу</h2>
                    <Form form={formInit} disabled={initData.loading} size={'large'} validateTrigger={'onFinish'} onFinish={onInit}>
                        <Form.Item name={'phone'} rules={[
                            {required: true, message: 'Пожалуйста заполните'},
                            {pattern: phoneRegex, message: 'Нужно заполнить полностью'}
                        ]}>
                            <MaskedInput mask={'+7 (000) 000-00-00'} placeholder={'Номер телефона'} className={cls.input}/>
                        </Form.Item>
                        <Form.Item name={'email'} rules={[
                            {required: true, message: 'Пожалуйста заполните'},
                            {type: 'email', message: 'Не соответствует формату email'}
                        ]}>
                            <Input placeholder={'Email'} className={cls.input}/>
                        </Form.Item>
                        <div className={cls.control}>
                            <T2Button loading={initData.loading} htmlType={'submit'} className={cls.button}>Подключить</T2Button>
                            <div className={cls.price}>15 ₽/сут</div>
                        </div>
                    </Form>
                    <div className={'caption'}>
                        Для подключения услуги введите код из&nbsp;SMS с&nbsp;номера 312, который мы&nbsp;направим на&nbsp;ваш номер телефона.
                        Нажимая на&nbsp;кнопку &laquo;Подключить&raquo;, вы&nbsp;принимаете условия оферты <a href={OFFER_ITK}>ООО &laquo;ИТ&nbsp;Консул&raquo;</a>, <a href={OFFER_T2}>Оферта&nbsp;t2</a>&nbsp;и&nbsp;соглашаетесь на&nbsp;<a href={OFFER_POLICY}>Обработку персональных данных</a>.
                    </div>
                </div>
            </Card>
            <div className={cls.imageWrapper} style={{backgroundImage:`url(${img_guard})`}}/>

            <Modal open={confirmOpen} onCancel={() => {setConfirmOpen(false); formConfirm.resetFields(); setErrorTitle(''); setErrorMessage('');}} footer={null}>
                <Space direction={'vertical'} size={24}>
                    <h2>Подтверждение</h2>
                    <Form form={formConfirm} onFinish={onConfirm} validateTrigger={'onFinish'} disabled={confirmData.loading}>
                        <div className={cls.codeForm}>
                            <Form.Item name={'code'} className={cls.item}
                            validateFirst={true}
                            validateStatus={errorTitle==='Не верный код' ? 'error' : undefined}
                            help={errorTitle==='Не верный код' ? errorMessage : undefined}
                            rules={[
                                {required: true, message: 'Пожалуйста заполните'},
                                {pattern: /^\d{4}$/, message: 'Только 4 цифры'},
                            ]}>
                                <Input placeholder={'Код из SMS'} className={cls.code} onChange={() => {setErrorTitle(''); setErrorMessage('');}}/>
                            </Form.Item>
                            <Form.Item className={cls.item}>
                                <T2Button loading={confirmData.loading} htmlType={'submit'} className={cls.button}>Подключить</T2Button>
                            </Form.Item>
                        </div>
                    </Form>
                    <div className={cn(['caption', cls.legal])}>
                        Нажимая на&nbsp;кнопку &laquo;Подключить&raquo;, вы&nbsp;принимаете условия оферты <a
                        href={OFFER_ITK}>ООО &laquo;ИТ&nbsp;Консул&raquo;</a>, <a href={OFFER_T2}>Оферта&nbsp;t2</a>&nbsp;и&nbsp;соглашаетесь на&nbsp;<a href={OFFER_POLICY}>Обработку персональных данных</a>.
                    </div>
                </Space>
            </Modal>

            <Modal open={successOpen} onCancel={() => setSuccessOpen(false)} footer={null}>
                <Space direction={'vertical'} size={24}>
                    <h2>Успешно</h2>
                    <div>Подписка успешно оформлена, сертификат с&nbsp;подробной информацией направлены на&nbsp;указанную электронную почту</div>
                    <T2Button onClick={() => setSuccessOpen(false)}>Хорошо</T2Button>
                </Space>
            </Modal>

            <Modal open={errorOpen} onCancel={() => setErrorOpen(false)} footer={null}>
                <Space direction={'vertical'} size={24}>
                    <h2>{errorTitle}</h2>
                    <div>{errorMessage}</div>
                    <T2Button onClick={() => setErrorOpen(false)}>Закрыть</T2Button>
                </Space>
            </Modal>

        </div>
    )
}